import React from 'react'
import { Redirect, Route as ReactRouterRoute, Switch as ReactRouterSwitch } from 'react-router-dom'
import qs from 'qs'
import { RouteInterface, RoutePath, CoreRoute } from 'lib/types'
import { useCoreContext } from 'lib/core/hooks'
import { PrivateRoute } from 'lib/core/components/PrivateRoute'
import { ProcessPaypalExpress } from 'lib/core/hooks/usePaypalExpress/scenes/ProcessPaymentMethod'

interface Props {
  children: React.ReactNode
  entityResolver: React.ComponentType<any>
}

export const Switch = ({ children, entityResolver }: Props) => {
  const { path, route } = useCoreContext()
  const routes = createRoutes({ path, route })

  return (
    <ReactRouterSwitch>
      {children}
      <ReactRouterRoute
        exact
        path='/customer/account/createPassword'
        render={({ location }) => {
          const { search } = location
          const { token } = qs.parse(search.substr(1))
          return (
            <Redirect
              to={{
                pathname: path.resetPassword,
                state: { token }
              }}
            />
          )
        }}
      />

      <ReactRouterRoute
        path='/customer/account/confirm'
        render={({ location }) => {
          const { search } = location
          const { key, id } = qs.parse(search.substr(1))
          return (
            <Redirect
              to={{
                pathname: path.confirmAccount,
                state: { token: key, userId: id }
              }}
            />
          )
        }}
      />

      {routes.map((route) => {
        const { auth, path, exact, component } = route
        const RouteComponent = auth ? PrivateRoute : ReactRouterRoute
        return (
          <RouteComponent
            key={path}
            exact={exact}
            path={path}
            component={component}
          />
        )
      })}

      <Redirect exact path='/login' to={path.login} />
      <Redirect exact path='/wishlist' to='/customer/account/wishlist' />
      <ReactRouterRoute path='*' component={entityResolver} />
    </ReactRouterSwitch>
  )
}

const createRoutes = (params: { path: RoutePath, route?: RouteInterface }): CoreRoute[] => {
  const { route, path } = params
  return [
    {
      component: route?.createAccount.component ?? createUndefinedComponent('route.createAccount.component'),
      path: path.createAccount,
      auth: route?.createAccount?.auth,
      exact: route?.createAccount?.exact ?? true
    },
    {
      component: route?.confirmAccount.component ?? createUndefinedComponent('route.confirmAccount.component'),
      path: path.confirmAccount,
      auth: route?.confirmAccount?.auth,
      exact: route?.confirmAccount?.exact ?? true
    },
    {
      component: route?.forgotPassword.component ?? createUndefinedComponent('route?.forgotPassword.component'),
      path: path.forgotPassword,
      auth: route?.forgotPassword?.auth,
      exact: route?.forgotPassword?.exact ?? true
    },
    {
      component: route?.resetPassword.component ?? createUndefinedComponent('route?.resetPassword.component'),
      path: path.resetPassword,
      auth: route?.resetPassword?.auth,
      exact: route?.resetPassword?.exact ?? true
    },
    {
      component: route?.login.component ?? createUndefinedComponent('route?.login.component'),
      path: path.login,
      auth: route?.login?.auth,
      exact: route?.login?.exact ?? true
    },
    {
      component: route?.cart.component ?? createUndefinedComponent('route?.cart.component'),
      path: path.cart,
      auth: route?.cart?.auth,
      exact: route?.cart?.exact
    },
    {
      component: route?.checkout.component ?? createUndefinedComponent('route?.checkout.component'),
      path: path.checkout,
      auth: route?.checkout?.auth,
      exact: route?.checkout?.exact
    },
    {
      component: route?.customer.component ?? createUndefinedComponent('route?.customer.component'),
      path: path.customer,
      auth: route?.customer?.auth,
      exact: route?.customer?.exact
    },
    {
      component: route?.search.component ?? createUndefinedComponent('route?.search.component'),
      path: path.search,
      auth: route?.search?.auth,
      exact: route?.search?.exact
    },
    {
      component: route?.paypalExpress?.component ?? ProcessPaypalExpress,
      path: path.paypalExpress,
      auth: route?.paypalExpress?.auth,
      exact: route?.paypalExpress?.exact
    }
  ]
}

const createUndefinedComponent = (name: string) => () => {
  throw new Error(`${name} is undefined.`)
}
